import React, { useState } from 'react';
import { ThemeProvider } from 'styled-components';
import original from 'react95/dist/themes/original';
import blue from 'react95/dist/themes/blue';
import ClientDetailsForm from './ClientDetailsForm';
import { Button, Window, WindowHeader, WindowContent, Select } from 'react95';
import { createClient } from '../models/Client';

/**
 * Demo component for ClientDetailsForm
 * Showcases all the features implemented in task 4
 */
const ClientDetailsFormDemo = () => {
  const [theme, setTheme] = useState(original);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showFinancialSummary, setShowFinancialSummary] = useState(true);
  const [demoMode, setDemoMode] = useState('new'); // 'new', 'existing', 'search'

  // Sample client data for demo
  const sampleClients = [
    createClient({
      clientName: 'John Smith',
      debtCode: 'CC01',
      homeAddress: '123 Main St, Anytown, ST 12345',
      phone: '555-123-4567',
      workPhone: '555-987-6543',
      cell: '555-555-5555',
      email: 'john.smith@email.com',
      contact: 'John Smith',
      drLic: 'D123456789',
      ssn: '123-45-6789',
      acctRep: 'Bob Johnson',
      alias: 'Johnny',
      comment: 'Prefers phone contact in the morning'
    }),
    createClient({
      clientName: 'Jane Doe',
      debtCode: 'PL02',
      homeAddress: '456 Oak Ave, Another City, ST 67890',
      phone: '555-234-5678',
      email: 'jane.doe@email.com',
      contact: 'Jane Doe',
      acctRep: 'Sarah Wilson',
      comment: 'Email preferred for communication'
    }),
    createClient({
      clientName: 'Robert Johnson',
      debtCode: 'AU03',
      homeAddress: '789 Pine Rd, Third Town, ST 11111',
      phone: '555-345-6789',
      workPhone: '555-876-5432',
      email: 'robert.j@email.com',
      contact: 'Bob Johnson',
      drLic: 'D987654321',
      acctRep: 'Mike Davis'
    })
  ];

  const handleClientSaved = (client) => {
    console.log('Client saved:', client);
    alert(`Client "${client.clientName}" saved successfully!`);
  };

  const handleClientSelected = (client) => {
    console.log('Client selected:', client);
    setSelectedClient(client);
  };

  const loadSampleClient = (index) => {
    setSelectedClient(sampleClients[index]);
    setDemoMode('existing');
  };

  const startNewClient = () => {
    setSelectedClient(null);
    setDemoMode('new');
  };

  const toggleTheme = () => {
    setTheme(theme === original ? blue : original);
  };

  return (
    <ThemeProvider theme={theme}>
      <div style={{ padding: '20px', backgroundColor: '#c0c0c0', minHeight: '100vh' }}>
        
        {/* Demo Controls */}
        <Window style={{ width: '100%', marginBottom: '20px' }}>
          <WindowHeader>
            <span>🎮 Client Details Form Demo - Task 4 Implementation</span>
          </WindowHeader>
          <WindowContent style={{ padding: '15px' }}>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Demo Mode:</span>
                <Button 
                  onClick={startNewClient}
                  active={demoMode === 'new'}
                  size="sm"
                >
                  New Client
                </Button>
                <Button 
                  onClick={() => loadSampleClient(0)}
                  active={demoMode === 'existing'}
                  size="sm"
                >
                  Load Sample 1
                </Button>
                <Button 
                  onClick={() => loadSampleClient(1)}
                  size="sm"
                >
                  Load Sample 2
                </Button>
                <Button 
                  onClick={() => loadSampleClient(2)}
                  size="sm"
                >
                  Load Sample 3
                </Button>
              </div>

              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Theme:</span>
                <Button onClick={toggleTheme} size="sm">
                  {theme === original ? 'Switch to Blue' : 'Switch to Original'}
                </Button>
              </div>

              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Financial Summary:</span>
                <Button 
                  onClick={() => setShowFinancialSummary(!showFinancialSummary)}
                  active={showFinancialSummary}
                  size="sm"
                >
                  {showFinancialSummary ? 'Hide' : 'Show'}
                </Button>
              </div>
            </div>

            {/* Feature Checklist */}
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f0f0f0', border: '1px inset #c0c0c0' }}>
              <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>
                ✅ Task 4 Features Implemented:
              </div>
              <div style={{ fontSize: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                <div>• Comprehensive client form with all required fields</div>
                <div>• Debt code dropdown with available options</div>
                <div>• Form validation and error display</div>
                <div>• Integration with financial summary display</div>
                <div>• Save, clear, and navigation functionality</div>
                <div>• Client search by name and debt code</div>
                <div>• Real-time validation feedback</div>
                <div>• Windows 95 themed interface</div>
              </div>
            </div>
          </WindowContent>
        </Window>

        {/* Main Form */}
        <ClientDetailsForm
          initialClient={selectedClient}
          onClientSaved={handleClientSaved}
          onClientSelected={handleClientSelected}
          showFinancialSummary={showFinancialSummary}
        />

        {/* Instructions */}
        <Window style={{ width: '100%', marginTop: '20px' }}>
          <WindowHeader>
            <span>📋 Demo Instructions</span>
          </WindowHeader>
          <WindowContent style={{ padding: '15px' }}>
            <div style={{ fontSize: '11px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>How to test the features:</div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong>1. Form Validation:</strong> Try saving without filling required fields (Client Name, Debt Code)
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong>2. Input Validation:</strong> Enter invalid email (no @) or phone (with special chars like !)
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong>3. Debt Code Dropdown:</strong> Click the Debt Code field to see available options
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong>4. Search Functionality:</strong> Click "Search" button and type client names or debt codes
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong>5. Clear/New:</strong> Use Clear button to reset form, New button for fresh start
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong>6. Financial Summary:</strong> Toggle on/off to see integrated financial display
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong>7. Sample Data:</strong> Load sample clients to see populated forms
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong>8. Theme Support:</strong> Switch between Windows 95 themes
              </div>
            </div>
          </WindowContent>
        </Window>
      </div>
    </ThemeProvider>
  );
};

export default ClientDetailsFormDemo;